import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import simulationService from '../services/simulationService';
import api from '../services/api';

const AuthContext = createContext();

const PROD_DOMAIN = 'vidal-app.com';
const DEV_DOMAIN = 'alpha.vidal-app.com';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);

  useEffect(() => {
    // Receber dados de auth transferidos de outro domínio (alpha → prod)
    try {
      const hash = window.location.hash;
      if (hash.startsWith('#auth-transfer=')) {
        const encoded = hash.replace('#auth-transfer=', '');
        const transferData = JSON.parse(atob(decodeURIComponent(encoded)));
        if (transferData.vidal_auth) localStorage.setItem('vidal_auth', transferData.vidal_auth);
        if (transferData.vidal_user) localStorage.setItem('vidal_user', transferData.vidal_user);
        if (transferData.vidal_selected_project) localStorage.setItem('vidal_selected_project', transferData.vidal_selected_project);
        if (transferData.vidal_current_project_data) localStorage.setItem('vidal_current_project_data', transferData.vidal_current_project_data);
        window.history.replaceState(null, '', window.location.pathname);
      }
    } catch (e) {
      console.error('Erro ao receber auth transfer:', e);
    }

    checkAuth();
    // Carregar projeto do localStorage na inicialização (para não ficar sem
    // nada na tela enquanto a chamada de rede abaixo não volta) e, em
    // seguida, buscar os dados atualizados da API — o snapshot do
    // localStorage pode estar desatualizado (ex: Acessos do projeto editados
    // em outra aba/sessão, ou simplesmente há muito tempo sem trocar de
    // projeto).
    try {
      const storedProject = localStorage.getItem('vidal_current_project_data');
      if (storedProject) {
        setCurrentProject(JSON.parse(storedProject));
      }
    } catch (e) {
      console.error('Erro ao carregar projeto inicial:', e);
    }
    refreshCurrentProject();
  }, []);

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const isSimulating = !!localStorage.getItem('simulatedUserId');

        // Em simulação não usa o usuário em cache (GOD); espera o /auth/me retornar o simulado
        if (!isSimulating) {
          const userData = authService.getUser();
          setUser(userData);
          setIsAuthenticated(true);
        }

        // Valida o token com o backend. IMPORTANTE: só desloga em falha de
        // autenticação CONFIRMADA (401/403). Erros transitórios (rate limit
        // 429, rede instável, backend fora do ar por um instante) não devem
        // encerrar a sessão — o usuário continua logado com os dados em
        // cache até a próxima verificação bem-sucedida.
        const result = await authService.getMe();
        if (result.success) {
          setUser(result.user);
          // IMPORTANTE: setIsAuthenticated também precisa rodar aqui (não só
          // no bloco `!isSimulating` acima). Durante uma simulação, um reload
          // de página (ex: ao trocar de projeto) cai direto neste branch —
          // sem isso, `isAuthenticated` nunca vira true e o ProtectedRoute
          // mostra a tela de login, dando a impressão de que o usuário foi
          // deslogado quando na verdade só a troca de projeto recarregou a página.
          setIsAuthenticated(true);
        } else if (result.status === 401 || result.status === 403) {
          console.log('Token inválido ou expirado, deslogando usuário');
          logout();
        } else {
          console.warn(
            'Não foi possível validar o token agora (erro transitório, sessão mantida):',
            result.status || result.error
          );
        }
      }
    } catch (error) {
      // authService.getMe() já trata seus próprios erros internamente e
      // não deveria lançar — este catch é só uma rede de segurança e
      // propositalmente NÃO desloga o usuário.
      console.error('Check auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const result = await authService.login(email, password);
    if (result.success) {
      // Se está em alpha (dev) e não é god, redirecionar para produção
      const hostname = window.location.hostname;
      if (hostname === DEV_DOMAIN && result.user?.cargo !== 'god') {
        const transferData = btoa(JSON.stringify({
          vidal_auth: localStorage.getItem('vidal_auth'),
          vidal_user: localStorage.getItem('vidal_user'),
          vidal_selected_project: localStorage.getItem('vidal_selected_project'),
          vidal_current_project_data: localStorage.getItem('vidal_current_project_data'),
        }));
        // Limpar localStorage do alpha (não precisa ficar logado aqui)
        authService.logout();
        window.location.href = `https://${PROD_DOMAIN}/#auth-transfer=${encodeURIComponent(transferData)}`;
        return { success: true, user: result.user, redirected: true };
      }
      setUser(result.user);
      setIsAuthenticated(true);
    }
    return result;
  };

  const register = async (registerData) => {
    const result = await authService.register(registerData);
    if (result.success && result.autoLogin) {
      setUser(result.user);
      setIsAuthenticated(true);
    }
    return result;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setCurrentProject(null);
    localStorage.removeItem('vidal_current_project_data');
    localStorage.removeItem('vidal_active_screen');
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('vidal_user', JSON.stringify(userData));
  };

  /**
   * Entra no modo simulação sem dar reload.
   * Salva o projeto original do GOD, ajusta localStorage e busca o usuário simulado via /auth/me.
   */
  const enterSimulation = async (userId, userData, projectIds) => {
    setLoading(true);
    try {
      const originalProjectId = localStorage.getItem('vidal_selected_project');
      const originalProjectData = localStorage.getItem('vidal_current_project_data');

      if (originalProjectId) {
        localStorage.setItem('vidal_project_before_simulation', originalProjectId);
      }
      if (originalProjectData) {
        localStorage.setItem('vidal_project_data_before_simulation', originalProjectData);
      }

      localStorage.setItem('simulatedUserId', userId);
      localStorage.setItem('simulatedUserInfo', JSON.stringify({
        id: userId,
        name: userData.name || 'Usuário',
        email: userData.email || '',
        cargo: userData.cargo || '',
        color: userData.color || '',
        projectIds: projectIds || []
      }));

      // Sempre resolver o projeto selecionado a partir dos projetos do usuário
      // simulado. Se não for feito, 'vidal_selected_project' pode continuar
      // apontando para o projeto do GOD (caso o usuário simulado não tenha
      // nenhum projeto), fazendo o header mostrar uma empresa à qual o
      // usuário emulado não tem acesso, enquanto todas as telas de dados
      // negam a permissão — dando a impressão de que "não carrega o projeto".
      if (projectIds?.length > 0) {
        localStorage.setItem('vidal_selected_project', projectIds[0]);
      } else {
        localStorage.removeItem('vidal_selected_project');
      }

      // Evita dados stale do projeto do GOD; StatusBar refetcha o projeto do simulado
      localStorage.removeItem('vidal_current_project_data');
      setCurrentProject(null);

      const result = await authService.getMe();
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
      } else {
        throw new Error('Falha ao carregar usuário simulado');
      }
    } catch (error) {
      console.error('Erro ao entrar em simulação:', error);
      if (error.response?.status === 401 || error.message?.includes('Token')) {
        logout();
      } else {
        // Rollback localStorage em caso de erro não fatal
        const originalProjectId = localStorage.getItem('vidal_project_before_simulation');
        const originalProjectData = localStorage.getItem('vidal_project_data_before_simulation');
        if (originalProjectId) {
          localStorage.setItem('vidal_selected_project', originalProjectId);
        }
        if (originalProjectData) {
          localStorage.setItem('vidal_current_project_data', originalProjectData);
        }
        localStorage.removeItem('simulatedUserId');
        localStorage.removeItem('simulatedUserInfo');
        localStorage.removeItem('vidal_project_before_simulation');
        localStorage.removeItem('vidal_project_data_before_simulation');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sai do modo simulação sem dar reload.
   * Restaura o projeto original do GOD e busca o usuário real via /auth/me.
   */
  const exitSimulation = async () => {
    setLoading(true);
    try {
      await simulationService.stopSimulation();

      const originalProjectId = localStorage.getItem('vidal_project_before_simulation');
      const originalProjectData = localStorage.getItem('vidal_project_data_before_simulation');

      if (originalProjectId) {
        localStorage.setItem('vidal_selected_project', originalProjectId);
      } else {
        localStorage.removeItem('vidal_selected_project');
      }

      if (originalProjectData) {
        localStorage.setItem('vidal_current_project_data', originalProjectData);
        setCurrentProject(JSON.parse(originalProjectData));
      } else {
        localStorage.removeItem('vidal_current_project_data');
        setCurrentProject(null);
      }

      localStorage.removeItem('vidal_project_before_simulation');
      localStorage.removeItem('vidal_project_data_before_simulation');

      const result = await authService.getMe();
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
      } else {
        throw new Error('Falha ao carregar usuário real');
      }
    } catch (error) {
      console.error('Erro ao sair da simulação:', error);
      if (error.response?.status === 401 || error.message?.includes('Token')) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateProjectData = async (projectData) => {
    if (projectData) {
      setCurrentProject(projectData);
      localStorage.setItem('vidal_current_project_data', JSON.stringify(projectData));
      // Re-buscar user com contexto do novo projeto para resolver permissions do membro
      try {
        const result = await authService.getMe();
        if (result.success) {
          // Só atualizar state se permissions/role realmente mudaram (evita loop infinito)
          const newPerms = JSON.stringify(result.user.permissions || {});
          const oldPerms = JSON.stringify(user?.permissions || {});
          if (newPerms !== oldPerms || result.user.cargo !== user?.cargo) {
            setUser(result.user);
          }
        }
      } catch (e) {
        console.warn('Erro ao re-buscar permissões do usuário:', e);
      }
    } else {
      setCurrentProject(null);
      localStorage.removeItem('vidal_current_project_data');
    }
  };

  // Busca os dados atuais do projeto direto da API e atualiza o contexto
  // (state + localStorage). Sem isso, `currentProject.permissions` (usado
  // pelo `hasPermission` para o teto do projeto) fica preso ao snapshot
  // salvo no localStorage na última vez que o projeto foi selecionado —
  // editar os Acessos do projeto (ex: EditProjectModal) NÃO atualiza esse
  // cache, então o menu lateral continua mostrando permissões antigas até
  // o usuário trocar de projeto e voltar.
  const refreshCurrentProject = async (projectId) => {
    const id = projectId || localStorage.getItem('vidal_selected_project');
    if (!id) return;
    try {
      const response = await api.get(`/projects/${id}`);
      const project = response.data?.project || response.data;
      if (project) {
        await updateProjectData(project);
      }
    } catch (e) {
      console.warn('Erro ao atualizar dados do projeto atual:', e);
    }
  };

  // Helper: verifica se um valor de permissão concede acesso
  const _permissionGranted = (value, requiredLevel = 'view') => {
    if (value === undefined || value === null) return false;
    // Formato boolean (novo sistema)
    if (typeof value === 'boolean') return value;
    // Formato string (legado): none/view/edit/admin
    const levels = { none: 0, view: 1, edit: 2, admin: 3 };
    const current = levels[value] || 0;
    const required = levels[requiredLevel] || 0;
    return current >= required;
  };

  const hasPermission = (module, requiredLevel = 'view') => {
    if (!user) return false;

    // O usuário em contexto já reflete o simulado quando em modo simulação
    const effectiveCargo = user.cargo;

    // God tem acesso a tudo
    if (effectiveCargo === 'god') return true;
    
    // Restrição especial: apenas God pode acessar o módulo settings (Admin)
    if (module === 'settings' && requiredLevel === 'admin') {
      return effectiveCargo === 'god';
    }
    
    // Restrição especial: apenas God pode acessar o módulo projects
    if (module === 'projects') {
      return effectiveCargo === 'god';
    }
    
    // Restrição especial: apenas God pode acessar o NFe Downloader
    if (module === 'nfe_downloader') {
      return effectiveCargo === 'god';
    }
    
    // ── Interseção: projeto (teto) ∩ usuário (individual) ──
    // 1. Verificar permissão do PROJETO (teto — define módulos disponíveis).
    // Isso vale para TODO MUNDO, inclusive admin do projeto: se o módulo
    // está desligado nos Acessos do projeto, a tela não aparece — o teto
    // do projeto é a fonte da verdade do que aquela empresa contratou/tem
    // liberado, não algo que o admin do projeto possa contornar.
    const projectAllows = (() => {
      // Sem projeto carregado ainda (ex: instante inicial após entrar em
      // simulação, enquanto o StatusBar ainda busca os dados do projeto
      // simulado) → nega por padrão. Liberar tudo aqui era inconsistente
      // com o restante da função (que já nega quando falta uma chave
      // específica) e fazia o menu mostrar telas fora do teto do projeto
      // durante essa janela de carregamento.
      if (!currentProject || !currentProject.permissions) return false;
      const pv = currentProject.permissions[module];
      if (pv === undefined || pv === null) return false;
      return _permissionGranted(pv, requiredLevel);
    })();
    
    if (!projectAllows) return false;
    
    // Admin do projeto tem acesso total a todas as telas QUE O PROJETO
    // libera (acima), sem estar sujeito à permissão INDIVIDUAL dele —
    // essa granularidade de membro é só para usuários comuns.
    if (effectiveCargo === 'admin') {
      return true;
    }
    
    // 2. Verificar permissão do USUÁRIO (individual — definido no convite/edição)
    // Fallback é por MÓDULO, não pelo objeto inteiro: se o usuário tiver
    // permissões individuais definidas para OUTROS módulos mas não para este,
    // ele ainda herda o teto do projeto (já validado como permitido acima).
    // Sem isso, qualquer usuário com pelo menos uma permissão individual
    // ficava bloqueado em todas as telas que o projeto libera mas que não
    // estão explicitamente listadas no objeto de permissões dele.
    const userValue = user.permissions ? user.permissions[module] : undefined;
    if (userValue === undefined || userValue === null) {
      return true;
    }
    
    return _permissionGranted(userValue, requiredLevel);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        currentProject, // Exporting state if needed elsewhere
        login,
        register,
        logout,
        updateUser,
        checkAuth,
        hasPermission,
        updateProjectData,
        refreshCurrentProject,
        enterSimulation,
        exitSimulation
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
